// CandidateSkillSection.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Tooltip,
  alpha,
  useTheme,
  Dialog,
  DialogContent,
  Chip,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Tabs,
  Tab,
} from "@mui/material";
import {
  DeleteOutline,
  FileDownloadOutlined,
  InsertDriveFileOutlined,
  Visibility as ViewIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  Image as ImageIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useQuery, useQueryClient } from "react-query";
import { deleteCandidateSkill, getCandidateSkills } from "../../api/services/candidate";
import DataTable from "../Table/DataTable";
import ConfirmModal from "../ConfirmModal";
import { QK } from "../../base/qk";
import AddCandidateSkill from "../Candidate/AddCandidateSkill";

const CandidateSkillSection = ({ clickedId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [skillId, setSkillId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewTab, setPreviewTab] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);

  const { data: candidateSkills, isLoading } = useQuery(
    [QK.candidateSkills, clickedId],
    () => getCandidateSkills(clickedId),
    { enabled: !!clickedId }
  );

  const handleDelete = async () => {
    const response = await deleteCandidateSkill(skillId);
    if (response.status === 204) {
      enqueueSnackbar("Skill Deleted Successfully", { variant: "success" });
      queryClient.invalidateQueries([QK.candidateSkills, clickedId]);
      setShowConfirmModal(false);
    } else {
      enqueueSnackbar("Something went wrong", { variant: "error" });
      setShowConfirmModal(false);
    }
  };

  const handleConfirm = (id) => {
    setSkillId(id);
    setShowConfirmModal(true);
  };

  const handlePreview = (documents, title) => {
    if (!documents || documents.length === 0) {
      enqueueSnackbar("No documents to preview", { variant: "info" });
      return;
    }

    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp"];
    const images = documents.filter((url) => {
      const ext = url.split(".").pop()?.toLowerCase() || "";
      return imageExtensions.includes(ext);
    });

    const pdfs = documents.filter((url) => {
      const ext = url.split(".").pop()?.toLowerCase() || "";
      return ext === "pdf";
    });

    const others = documents.filter((url) => {
      const ext = url.split(".").pop()?.toLowerCase() || "";
      return !imageExtensions.includes(ext) && ext !== "pdf";
    });

    setPreviewData([
      { type: "images", items: images, label: `Images (${images.length})` },
      { type: "pdfs", items: pdfs, label: `PDFs (${pdfs.length})` },
      { type: "others", items: others, label: `Others (${others.length})` },
    ]);
    setPreviewTitle(title || "Documents");
    setPreviewTab(0);
    setPreviewOpen(true);
  };

  const handleDownload = (url) => {
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.download = url.split("/").pop() || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewData([]);
    setPreviewTitle("");
    setPreviewTab(0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getFileIcon = (url) => {
    const ext = url?.split(".").pop()?.toLowerCase() || "";
    if (ext === "pdf") return <PdfIcon sx={{ fontSize: 20 }} />;
    if (["doc", "docx"].includes(ext)) return <DocIcon sx={{ fontSize: 20 }} />;
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext))
      return <ImageIcon sx={{ fontSize: 20 }} />;
    return <InsertDriveFileOutlined sx={{ fontSize: 20 }} />;
  };

  const getFileColor = (url) => {
    const ext = url?.split(".").pop()?.toLowerCase() || "";
    if (ext === "pdf") return "#EF4444";
    if (["doc", "docx"].includes(ext)) return "#3B82F6";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "#10B981";
    return "#6B7280";
  };

  const isImageViewable = (url) => {
    const ext = url?.split(".").pop()?.toLowerCase() || "";
    return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
  };

  const columns = [
    { key: "skill", label: "Skill" },
    { key: "documents", label: "Documents" },
    { key: "expiry_date", label: "Expiry Date" },
    { key: "notes", label: "Notes" },
  ];

  const renderCell = (item, header) => {
    if (header.key === "skill") {
      return (
        <Typography variant="body2" fontWeight={600} color="#1A1A2E">
          {item.skill || "-"}
        </Typography>
      );
    }

    if (header.key === "documents") {
      const docs = item.documents || [];
      if (docs.length === 0) return <Typography variant="body2" color="#94A3B8">No documents</Typography>;

      const totalDocs = docs.length;

      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {docs.slice(0, 2).map((url, index) => (
              <Tooltip key={index} title={url.split("/").pop()} arrow>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "4px",
                    bgcolor: alpha(getFileColor(url), 0.1),
                    color: getFileColor(url),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                  }}
                >
                  {getFileIcon(url)}
                </Box>
              </Tooltip>
            ))}
          </Box>
          {docs.length > 2 && (
            <Chip
              label={`+${docs.length - 2}`}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.55rem",
                fontWeight: 600,
                bgcolor: "#F1F5F9",
                color: "#475467",
              }}
            />
          )}
          <Tooltip title={`Preview ${totalDocs} documents`} arrow>
            <IconButton
              size="small"
              onClick={() => handlePreview(docs, item.skill)}
              sx={{
                color: "#667085",
                p: 0.25,
                "&:hover": {
                  color: "#2469F0",
                  bgcolor: alpha("#2469F0", 0.06),
                },
              }}
            >
              <ViewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      );
    }

    if (header.key === "expiry_date") {
      return (
        <Typography variant="body2" color="#6B7280">
          {formatDate(item.expiry_date)}
        </Typography>
      );
    }

    if (header.key === "notes") {
      return (
        <Typography
          variant="body2"
          color="#6B7280"
          sx={{
            maxWidth: 180,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.notes || "-"}
        </Typography>
      );
    }

    return item[header.key] || "-";
  };

  const customActions = (item) => (
    <Stack direction="row" spacing={0.5} justifyContent="center">
      {item.documents && item.documents.length > 0 && (
        <Tooltip title="Preview Documents" arrow>
          <IconButton
            size="small"
            onClick={() => handlePreview(item.documents, item.skill)}
            sx={{
              color: "#667085",
              p: 0.5,
              "&:hover": {
                color: "#2469F0",
                bgcolor: alpha("#2469F0", 0.06),
              },
            }}
          >
            <ViewIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Delete" arrow>
        <IconButton
          size="small"
          onClick={() => handleConfirm(item.id)}
          sx={{
            color: "#667085",
            p: 0.5,
            "&:hover": {
              color: "#EF4444",
              bgcolor: alpha("#EF4444", 0.06),
            },
          }}
        >
          <DeleteOutline sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <Box sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
          Skills
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setShowAddModal(true)}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            bgcolor: "#2469F0",
            "&:hover": {
              bgcolor: "#1A5BD4",
            },
          }}
        >
          Add Skill
        </Button>
      </Box>

      <Dialog
        open={previewOpen}
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "90vh",
            bgcolor: "#1A1A2E",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid rgba(255,255,255,0.1)",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} color="white">
            {previewTitle} - Documents
          </Typography>
          <IconButton onClick={handleClosePreview} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {previewData.length > 0 && (
          <Tabs
            value={previewTab}
            onChange={(e, v) => setPreviewTab(v)}
            sx={{
              bgcolor: "rgba(255,255,255,0.05)",
              "& .MuiTab-root": {
                color: "rgba(255,255,255,0.7)",
                "&.Mui-selected": {
                  color: "white",
                },
              },
              "& .MuiTabs-indicator": {
                bgcolor: "#2469F0",
              },
            }}
          >
            {previewData.map((tab, index) => (
              <Tab
                key={index}
                label={`${tab.label}`}
                disabled={tab.items.length === 0}
              />
            ))}
          </Tabs>
        )}

        <DialogContent
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          {previewData.length > 0 && previewData[previewTab]?.items.length > 0 ? (
            previewData[previewTab].type === "images" ? (
              <ImageList cols={3} gap={12} sx={{ width: "100%", height: "70vh" }}>
                {previewData[previewTab].items.map((url, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={url}
                      alt={`Document ${index + 1}`}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "200px",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                    <ImageListItemBar
                      title={`Document ${index + 1}`}
                      actionIcon={
                        <Tooltip title="Download" arrow>
                          <IconButton
                            sx={{ color: "white" }}
                            onClick={() => handleDownload(url)}
                          >
                            <FileDownloadOutlined />
                          </IconButton>
                        </Tooltip>
                      }
                      sx={{
                        borderRadius: "0 0 8px 8px",
                        "& .MuiImageListItemBar-title": {
                          fontSize: "0.75rem",
                        },
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  gap: 2,
                }}
              >
                {previewData[previewTab].items.map((url, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      p: 2,
                      bgcolor: "rgba(255,255,255,0.05)",
                      borderRadius: "8px",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {getFileIcon(url)}
                      <Typography variant="body2" color="rgba(255,255,255,0.8)">
                        {url.split("/").pop()}
                      </Typography>
                    </Box>
                    <Tooltip title="Download" arrow>
                      <IconButton
                        sx={{ color: "white" }}
                        onClick={() => handleDownload(url)}
                      >
                        <FileDownloadOutlined />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
                gap: 2,
              }}
            >
              <InsertDriveFileOutlined
                sx={{ fontSize: 80, color: "rgba(255,255,255,0.3)" }}
              />
              <Typography variant="body1" color="rgba(255,255,255,0.7)">
                No documents found
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <DataTable
        data={candidateSkills || []}
        headers={columns}
        renderCell={renderCell}
        customActions={customActions}
        loading={isLoading}
      />

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleDelete}
        title="Delete Skill"
        body="Are you sure you want to delete this skill? This action cannot be undone."
        isDelete={true}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <AddCandidateSkill
        open={showAddModal}
        handleClose={() => setShowAddModal(false)}
        candidateId={clickedId}
      />
    </Box>
  );
};

export default CandidateSkillSection;